import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import _ from 'underscore';

import { ChartPalette } from 'components/charts/types/chart-palette.type';
import { BenchmarkTimeGrouping } from 'views/benchmark-analytics/types/benchmark-filters.type';
import { BenchmarkSentiment } from 'views/benchmark-analytics/types/benchmark-sentiment.type';

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

// One value and how much it counts toward an average. Callers drop anything that should not count before averaging.
export interface WeightedValue {
  value: number;
  weight: number;
}

// date is 'YYYY-MM-DD', which dayjs reads as local time, so no time zone moves a row into another day.
// Keys sort chronologically as strings: 2026-01-05, 2026-W02 (ISO week-year), 2026-01, 2026-Q1.
export const toPeriodKey = (date: string, grouping: BenchmarkTimeGrouping): string => {
  const day = dayjs(date);

  switch (grouping) {
    case 'day':
      return date;
    case 'week':
      return `${day.isoWeekYear()}-W${String(day.isoWeek()).padStart(2, '0')}`;
    case 'month':
      return date.slice(0, 7);
    case 'quarter':
      return `${day.year()}-Q${day.quarter()}`;
  }
};

// Σ(value × weight) / Σ weight, so heavier items pull the average harder. null when nothing has weight.
export const weightedAverage = (items: WeightedValue[]): number | null => {
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);

  return totalWeight > 0 ? items.reduce((sum, item) => sum + item.value * item.weight, 0) / totalWeight : null;
};

// Period key -> weighted average of the items dated in that period. Periods with no items are left out.
export const weightedAverageByPeriod = (
  items: (WeightedValue & { date: string })[],
  grouping: BenchmarkTimeGrouping
): Record<string, number | null> =>
  _.mapObject(_.groupBy(items, (item) => toPeriodKey(item.date, grouping)), (periodItems) => weightedAverage(periodItems));

// Higher is better. A delta that rounds to zero at the shown precision is neutral.
export const deltaSentiment = (delta: number | null, decimals: number): BenchmarkSentiment => {
  if (delta === null || Math.round(Math.abs(delta) * 10 ** decimals) === 0) return 'neutral';

  return delta > 0 ? 'good' : 'bad';
};

export const sentimentColor = (palette: ChartPalette, sentiment: BenchmarkSentiment): string => {
  if (sentiment === 'good') return palette.status.green;
  if (sentiment === 'bad') return palette.status.red;
  return palette.text.secondary;
};

// The wording comes from the sign of the delta, so a neutral delta reads as equal even when it is not exactly zero.
export const deltaLabelId = (delta: number | null, sentiment: BenchmarkSentiment): string | null => {
  if (delta === null) return null;
  if (sentiment === 'neutral') return 'benchmarkAnalytics.growthPage.kpi.equalMarket';
  return delta > 0 ? 'benchmarkAnalytics.growthPage.kpi.aboveMarket' : 'benchmarkAnalytics.growthPage.kpi.belowMarket';
};
